import ReactGA from 'react-ga4';
import * as XLSX from 'xlsx';
import {getPercents, nf} from './dataHelpers';

export const DEFAULT_STACKED_EXPORT_OPTIONS = {
  sourcesScope: 'all',
  metricsScope: 'all',
  includeTotals: true,
  includePercentOfTotal: false,
  includeComparison: false,
  includeMetadata: true,
  fileType: 'xlsx',
};

const getMetrics = (metricsConfig, metricsScope, enabledMetric) => {
  const metrics = Object.values(metricsConfig?.perSource || {});
  if (metricsScope === 'selected' && enabledMetric) {
    return metrics.filter(m => enabledMetric.includes(m.dataField));
  }
  return metrics;
};

const getActiveSources = (availableSourcesSorted, disabledSources) =>
  (availableSourcesSorted || []).filter(source => !disabledSources.includes(source));

const getPeriodTotals = (hasSourceFilter, totals, totalsOfEnabledSources) =>
  hasSourceFilter ? totalsOfEnabledSources : totals;

const metricValue = (value) => (nf(value) ? nf(value) : 0);

const getExportSources = (availableSourcesSorted, disabledSources, sourcesScope) =>
  sourcesScope === 'all'
    ? availableSourcesSorted || []
    : getActiveSources(availableSourcesSorted, disabledSources);

const useFilteredTotals = (sourcesScope, disabledSources) =>
  sourcesScope !== 'all' && disabledSources.length > 0;

const buildDailyLong = ({
  graphData,
  sources,
  metrics,
  useFilteredTotals: filteredTotals,
  includePercentOfTotal,
  includeTotals,
}) => {
  const rows = [];

  graphData.forEach(day => {
    sources.forEach(source => {
      const row = {Date: day.date, Source: source};
      metrics.forEach(metric => {
        row[metric.displayName] = metricValue(day[`${source} ${metric.dataField}`]);
        if (includePercentOfTotal && metric.cumulative && !metric.disableTotals) {
          const dayTotal = metricValue(
            filteredTotals ? day[`enabledSources_${metric.dataField}`] : day[metric.dataField]
          );
          row[`% of ${metric.displayName}`] = Number(getPercents(row[metric.displayName], dayTotal));
        }
      });
      rows.push(row);
    });

    if (includeTotals) {
      const row = {Date: day.date, Source: 'Total'};
      metrics.forEach(metric => {
        row[metric.displayName] = metricValue(
          filteredTotals ? day[`enabledSources_${metric.dataField}`] : day[metric.dataField]
        );
      });
      rows.push(row);
    }
  });

  return rows;
};

const buildSummary = ({
  periodTotals,
  sources,
  metrics,
  includePercentOfTotal,
  includeTotals,
}) => {
  const rows = sources.map(source => {
    const row = {Source: source};
    metrics.forEach(metric => {
      const value = metricValue(periodTotals?.perSource?.[`${source} ${metric.dataField}`]);
      row[metric.displayName] = value;
      if (includePercentOfTotal && metric.cumulative && !metric.disableTotals) {
        const total = metricValue(periodTotals?.[metric.dataField]);
        row[`% of ${metric.displayName}`] = Number(getPercents(value, total));
      }
    });
    return row;
  });

  if (includeTotals) {
    const row = {Source: 'Total'};
    metrics.forEach(metric => {
      row[metric.displayName] = metricValue(periodTotals?.[metric.dataField]);
    });
    rows.push(row);
  }

  return rows;
};

const buildMetadata = (context, sources, metrics, options) => {
  const rows = [{Field: 'Exported at', Value: new Date().toISOString()}];

  const add = (field, value) => {
    if (value !== undefined && value !== null && value !== '') {
      rows.push({Field: field, Value: String(value)});
    }
  };

  add('Report type', context?.type);
  add('Selection', context?.selection?.label);
  add('Period', context?.period?.label || `${context?.period?.startDate} – ${context?.period?.endDate}`);
  add('Period start', context?.period?.startDate);
  add('Period end', context?.period?.endDate);
  add('Comparison period', context?.comparisonPeriod?.label);
  add('Analytics grouping', context?.analyticsGrouping);
  add('Country filter', context?.analyticsCountry);
  add('Device filter', context?.analyticsDevice);
  add('Bidder filter', context?.analyticsBidder);
  add('Split mode', context?.groupingEnabled ? 'Grouped' : context?.prebidSplitMode);
  add('Sources scope', options.sourcesScope === 'all' ? 'All sources' : 'Selected sources only');
  add('Sources exported', sources.join(', '));
  add('Metrics scope', options.metricsScope === 'all' ? 'All metrics' : 'Selected metric only');
  add('Metrics exported', metrics.map(m => m.displayName).join(', '));
  add('Include totals', options.includeTotals ? 'Yes' : 'No');
  add('Include % of total', options.includePercentOfTotal ? 'Yes' : 'No');
  add('Include comparison', options.includeComparison ? 'Yes' : 'No');

  return rows;
};

const appendSheet = (workbook, name, rows) => {
  if (!rows.length) return;
  const sheet = XLSX.utils.json_to_sheet(rows);
  const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
  sheet['!cols'] = Array.from({length: range.e.c - range.s.c + 1}, () => ({wch: 22}));
  XLSX.utils.book_append_sheet(workbook, sheet, name.slice(0, 31));
};

const downloadCsv = (rows, fileName) => {
  const sheet = XLSX.utils.json_to_sheet(rows);
  const csv = XLSX.utils.sheet_to_csv(sheet);
  const link = document.createElement('a');
  link.href = URL.createObjectURL(new Blob([csv], {type: 'text/csv;charset=utf-8;'}));
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
};

/**
 * Export stacked report graph data to XLSX or CSV.
 */
export function exportStackedReportXls({
  graphData,
  metricsConfig,
  period,
  type,
  disabledSources = [],
  availableSourcesSorted = [],
  totals,
  totalsOfEnabledSources,
  comparisonGraphData,
  comparisonTotals,
  comparisonTotalsOfEnabledSources,
  enabledMetric,
  comparisonAvailable = false,
  context = {},
  options: optionOverrides = {},
}) {
  const options = {...DEFAULT_STACKED_EXPORT_OPTIONS, ...optionOverrides};
  const filteredTotals = useFilteredTotals(options.sourcesScope, disabledSources);
  const sources = getExportSources(availableSourcesSorted, disabledSources, options.sourcesScope);
  const metrics = getMetrics(metricsConfig, options.metricsScope, enabledMetric);
  const periodTotals = getPeriodTotals(filteredTotals, totals, totalsOfEnabledSources);
  const comparisonPeriodTotals = getPeriodTotals(
    filteredTotals,
    comparisonTotals,
    comparisonTotalsOfEnabledSources
  );

  ReactGA.event({
    category: 'Export to XLS',
    action: 'click',
    label: `${type}_stack`,
  });

  try {
    const buildDailyArgs = {
      graphData,
      sources,
      metrics,
      useFilteredTotals: filteredTotals,
      includePercentOfTotal: options.includePercentOfTotal,
      includeTotals: options.includeTotals,
    };

    const buildSummaryArgs = {
      periodTotals,
      sources,
      metrics,
      includePercentOfTotal: options.includePercentOfTotal,
      includeTotals: options.includeTotals,
    };

    const sheets = [];

    if (options.includeMetadata) {
      sheets.push({
        name: 'Metadata',
        rows: buildMetadata(context, sources, metrics, options),
      });
    }

    if (graphData?.length) {
      sheets.push({
        name: 'Daily',
        rows: buildDailyLong(buildDailyArgs),
      });
    }

    if (periodTotals) {
      sheets.push({
        name: 'Summary',
        rows: buildSummary(buildSummaryArgs),
      });
    }

    if (options.includeComparison && comparisonAvailable) {
      if (comparisonGraphData?.length) {
        sheets.push({
          name: 'Comparison daily',
          rows: buildDailyLong({...buildDailyArgs, graphData: comparisonGraphData}),
        });
      }
      if (comparisonPeriodTotals) {
        sheets.push({
          name: 'Comparison summary',
          rows: buildSummary({...buildSummaryArgs, periodTotals: comparisonPeriodTotals}),
        });
      }
    }

    const dataSheets = sheets.filter(sheet => sheet.name !== 'Metadata');
    const primaryRows = dataSheets[0]?.rows || [];

    if (!primaryRows.length) {
      alert('Nothing to export for the current selection.');
      return;
    }

    const fileName = `setupad_${type}_stack_${period?.startDate || 'data'}_${
      period?.endDate || 'export'
    }`;

    if (options.fileType === 'csv') {
      downloadCsv(primaryRows, `${fileName}.csv`);
      return;
    }

    const workbook = XLSX.utils.book_new();
    sheets.forEach(sheet => appendSheet(workbook, sheet.name, sheet.rows));
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  } catch (error) {
    console.error('Export error:', error);
    alert('Export failed. Please try again.');
  }
}
